import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Table } from 'reactstrap';
import { axiosGet } from '../../util/apiConfig';

const columns = [
  ['initial', 'Initial'],
  ['waitingOnCoach', 'Waiting on coach'],
  ['waitingOnParent', 'Waiting on parent'],
  ['availableCredits', 'Credits'],
  ['total', 'Total'],
];

// Reported by the API but not counted in the table; each needs someone to look at it.
const diagnosticNotices = [
  ['creditsWithoutRecord', 'credit(s) shown in parent balances without a credit record (cannot be booked)'],
  ['creditRecordsHiddenFromParent', 'unused credit record(s) beyond the parent\'s visible balance (cannot be booked)'],
  ['inactiveHoldingCredit', 'switched-off link(s) still holding a reserved credit'],
  ['needsReview', 'request(s) or credit(s) missing a booking link, class or valid credit reference, blocked by another request, or whose cancelled class was restored'],
  ['expiredAvailableCredits', 'expired credit record(s) awaiting cleanup (cannot be booked)'],
  ['balanceMismatchSections', 'section(s) whose balance differs from their credit records (credits there are counted up to the balance)'],
];

export default function OutstandingMakeups({ classId }) {
  const [attempt, setAttempt] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setReport(null);
    const query = classId ? `?classId=${encodeURIComponent(classId)}` : '';
    axiosGet(`makeup/outstanding-by-coach${query}`)
      .then((response) => {
        if (!active) return;
        if (response.isError || !response.data || !Array.isArray(response.data.result)) {
          setError(true);
        } else {
          setReport(response.data);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });
    return () => { active = false; };
  }, [classId, attempt]);

  const diagnostics = (report && report.diagnostics) || {};
  const notices = diagnosticNotices
    .filter(([key]) => diagnostics[key] > 0)
    .map(([key, text]) => `${diagnostics[key]} ${text}`);

  return (
    <Card className="shadow border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
      <CardHeader className="border-0 bg-transparent py-4">
        <h3 className="mb-0">Outstanding Makeups by Coach</h3>
        <p className="text-muted small mb-0 mt-2">Live requests and unused, unexpired credits the parent can book (capped at their visible balance). Approved, expired and switched-off items are excluded.</p>
      </CardHeader>
      <CardBody aria-busy={loading}>
        {loading && <p role="status">Loading outstanding makeups…</p>}
        {error && <div role="alert">
          <p>Could not load outstanding makeups.</p>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setAttempt((value) => value + 1)}>Retry</button>
        </div>}
        {report && <>
          {notices.length > 0 && <div role="status" className="alert alert-warning small">
            <p className="mb-1">Needs checking (not included in the table unless noted):</p>
            <ul className="mb-0 pl-3">{notices.map((notice) => <li key={notice}>{notice}</li>)}</ul>
          </div>}
          {report.result.length === 0 ? <p>No outstanding makeups for this selection.</p> : (
            <div style={{ maxHeight: '500px', overflow: 'auto' }}>
              <Table className="align-items-center table-flush" responsive size="sm">
                <thead className="thead-light"><tr>
                  <th scope="col">Coach</th>
                  {columns.map(([key, label]) => <th key={key} scope="col" style={{ whiteSpace: 'normal' }}>{label}</th>)}
                </tr></thead>
                <tbody>{report.result.map((coach) => <tr key={coach.teacherId || 'unassigned'}>
                  <th scope="row" style={{ whiteSpace: 'normal' }}>{coach.name}</th>
                  {columns.map(([key]) => <td key={key}>{coach[key]}</td>)}
                </tr>)}</tbody>
              </Table>
            </div>
          )}
        </>}
      </CardBody>
    </Card>
  );
}
